import type {RichMessage} from "../diagnostics/types";

export type TicketCostBucketKey = "time" | "freight" | "load" | "tax" | "specific";

export type TicketCostBucket = {
  key: TicketCostBucketKey;
  label: string;
  amount: number;
};

export type TicketCostResolution = {
  state: "system" | "adjusted" | "override";
  label: string;
  systemValue?: string;
  appliedValue?: string;
  reason?: string;
};

export type TicketCostComponent = {
  /** Database code returned by ObjectCostRateModel.costComponentType. */
  code: string;
  label: string;
};

export type TicketCostAdjustment = {
  id: string;
  label: string;
  amount: number;
};

export type TicketCostWellReference = {
  wellId: string;
  wellName: string;
  allocation: number;
  amount: number;
};

/** Cost-input decision returned for one calculated charge/rate detail. */
export type TicketCostInputDecision = {
  inputKey: string;
  name: string;
  decision: "APPLIED" | "REJECTED" | "ERROR" | "IGNORED" | "REVIEWED";
  rateName?: string;
  rateDetailDescription?: string;
  systemValue?: string;
  userValue?: string;
  appliedValue?: string;
  systemCost?: number;
  candidateCost?: number;
  appliedCost?: number;
  reason?: string;
};

export type TicketCostLine = {
  id: string;
  label: string;
  bucket: TicketCostBucketKey;
  component?: TicketCostComponent;
  quantity?: number;
  quantityLabel?: string;
  /** Raw ObjectCostRateModel.billableQty, kept separate from the bucket calculation basis. */
  billableQuantity?: number;
  rate?: number;
  rateUnit?: string;
  amount: number;
  wellAllocations?: TicketCostWellReference[];
  /** Well that directly owns this charge; distinct from any downstream allocation split. */
  wellId?: string;
  wellName?: string;
  wellAfe?: string;
  allocation?: number;
  resolution?: TicketCostResolution;
  adjustments?: TicketCostAdjustment[];
  costInputs?: TicketCostInputDecision[];
};

export type TicketCostAllocation = {
  id: string;
  wellName: string;
  allocation: number;
  amount: number;
  lineCount: number;
  afe?: string;
};

export type TicketCostInvoice = {
  id: string;
  number: string;
  status: "draft" | "approved" | "sent" | "paid" | "void";
  amount: number;
  invoiceDate?: string;
};

export type TicketCostPreviewMeta = {
  /** Human-readable sources used to calculate an estimate before the ticket is posted. */
  sourceLabel: string;
  updatedAt?: string;
  comparisonLabel?: string;
  comparisonTotal?: number;
  notice?: string;
};

export type TicketCostVersionKind = "current" | "preview" | "historical";

export type TicketCostInputValueType = "NUMBER" | "BOOLEAN" | "TEMPORAL" | "STRING";

export type TicketCostInputDefinition = {
  id: string;
  inputKey: string;
  name: string;
  valueType: TicketCostInputValueType;
  unitOfMeasure?: string;
  unitOfMeasureType?: string;
};

export type TicketCostInputValue = {
  id?: string;
  sourceType: "USER" | "SYSTEM";
  reason?: string;
  numberValue?: number;
  booleanValue?: boolean;
  temporalValue?: number;
  stringValue?: string;
};

export type TicketCostInput = {
  definition: TicketCostInputDefinition;
  value?: TicketCostInputValue;
};

export type BillingDecisionTrace = {
  entityNames?: Record<string, string>;
  evaluationId: string;
  version: number;
  effectiveDttm: number | null;
  inputs: Record<string, string>;
  steps: Array<{code: string; outcome: string; entityId: string | null; explanation: string; values: Record<string, string>; message?: RichMessage | null}>;
  intervals: Array<{wellId: string | null; eventId: string | null; stageId: string | null; stageName: string | null;
    startDttm: number | null; endDttm: number | null; synthetic: boolean; outcome: string}>;
};

export type TicketCostBillingLocation = {
  decisionTrace?: BillingDecisionTrace;
  id?: string;
  name: string;
  type?: string;
  billingSide?: "pickup" | "delivery";
  afe?: string;
  afePrefix?: string;
  afeSuffix?: string;
  afeSource?: string;
  afeSourceLabel?: string;
  mappedFrom?: string;
  resolution?: string;
};

export type TicketCostRateModel = {
  id: string;
  name: string;
  /** Stable key shared by current, preview, and historical versions of the same rate model. */
  rateModelKey?: string;
  /** Label for the rate-model tab; defaults to name. */
  rateModelLabel?: string;
  /** Optional concise label used by the version selector. */
  versionOptionLabel?: string;
  versionKind?: TicketCostVersionKind;
  company?: string;
  modelType?: string;
  versionLabel?: string;
  historical?: boolean;
  financialLabel?: string;
  total: number;
  currency?: string;
  calculatedAt?: string;
  billingLocationDetails?: TicketCostBillingLocation;
  /** @deprecated Prefer billingLocationDetails. */
  billingLocation?: string;
  /** @deprecated Prefer billingLocationDetails.resolution. */
  billingLocationResolution?: string;
  /** Present only for an ephemeral estimate calculated from unposted ticket data. */
  preview?: TicketCostPreviewMeta;
  buckets: TicketCostBucket[];
  lines: TicketCostLine[];
  costInputs?: TicketCostInput[];
  allocations?: TicketCostAllocation[];
  invoices?: TicketCostInvoice[];
};

const ticketCostBucketOrder: Record<TicketCostBucketKey, number> = {
  time: 0,
  freight: 1,
  load: 2,
  tax: 3,
  specific: 4,
};

const compareTicketCostText = (left?: string, right?: string) => {
  const normalizedLeft = left?.trim().toLocaleLowerCase("en-US") ?? "";
  const normalizedRight = right?.trim().toLocaleLowerCase("en-US") ?? "";
  return normalizedLeft < normalizedRight ? -1 : normalizedLeft > normalizedRight ? 1 : 0;
};

/** Stable display order independent of API/database row order. */
export const compareTicketCostLines = (left: TicketCostLine, right: TicketCostLine) =>
  ticketCostBucketOrder[left.bucket] - ticketCostBucketOrder[right.bucket]
  || compareTicketCostText(left.component?.label, right.component?.label)
  || compareTicketCostText(left.label, right.label)
  || compareTicketCostText(left.wellName, right.wellName)
  || compareTicketCostText(left.wellId, right.wellId)
  || compareTicketCostText(left.id, right.id);

export const sortTicketCostLines = (lines: TicketCostLine[]) => [...lines].sort(compareTicketCostLines);

export const formatTicketCostCurrency = (value: number, currency = "USD") => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value);

export const formatTicketCostRate = (value: number | undefined, currency = "USD", unit?: string) => {
  if (value === undefined) return "—";
  if (unit === "%") return `${new Intl.NumberFormat("en-US", {maximumFractionDigits: 3}).format(value)}%`;
  const formatted = formatTicketCostCurrency(value, currency);
  return unit ? `${formatted} / ${unit}` : formatted;
};

export const formatTicketCostPercent = (value: number) => new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: value > 0 && value < 0.01 ? 2 : 1,
  maximumFractionDigits: 2,
}).format(value);
